import { useCallback, type Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { featureFlags } from "../../config/featureFlags";
import { QuestionEvidenceBindingPanel } from "../../components/compliance-workflow/questions/QuestionEvidenceBindingPanel";
import ccStyles from "../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss";
import {
  deriveGdprAssessmentTone,
  NIS2_ENTERPRISE_COMMON_PROBLEMS,
  NIS2_ENTERPRISE_PRIMARY_RECORDS,
  NIS2_ENTERPRISE_STRONG_INDICATORS,
  NIS2_ENTERPRISE_WEAK_INDICATORS,
  parseSemicolonBulletList,
  type GdprEnterpriseTone,
} from "./journeyWorkbenchHelpers";

const JOURNEY_PRIMARY_BTN = "!bg-green-600 hover:!bg-green-700 !border-green-600";
const NIS2_GE = "complianceCenterUi.journey.nis2Enterprise";

type Lang = "en" | "ru" | "es";
type LocTriple = { en?: string; ru?: string; es?: string };

function pickLoc(trip: LocTriple | undefined, lang: Lang): string {
  if (!trip) return "";
  return String(trip[lang] || trip.en || "");
}

function nis2OperationalStatusLabel(tone: GdprEnterpriseTone, t: (key: string, fallback: string) => string): string {
  if (tone === "green") return t(`${NIS2_GE}.operationalGreen`, "Operationally stable");
  if (tone === "red") return t(`${NIS2_GE}.operationalRed`, "Immediate cybersecurity action required");
  return t(`${NIS2_GE}.operationalAmber`, "Operational attention required");
}

function nis2RecordsStatusLabel(tone: GdprEnterpriseTone, t: (key: string, fallback: string) => string): string {
  if (tone === "green") return t(`${NIS2_GE}.recordsComplete`, "Operational records in place");
  if (tone === "red") return t(`${NIS2_GE}.recordsMissing`, "Operational records still needed");
  if (tone === "amber") return t(`${NIS2_GE}.recordsWeak`, "Records may need strengthening");
  return t(`${NIS2_GE}.recordsNotRequired`, "Not required for this answer");
}

function nis2ToneExecutiveClass(tone: GdprEnterpriseTone): string {
  if (tone === "green") return ccStyles.nis2ExecutiveValueGreen;
  if (tone === "red") return ccStyles.nis2ExecutiveValueRed;
  return ccStyles.nis2ExecutiveValueAmber;
}

function EvidenceChips({ items }: { items: string[] }) {
  if (!items.length) return <p className="text-xs text-slate-500">—</p>;
  return (
    <div className={ccStyles.gdprEvidenceChipList}>
      {items.map((item) => (
        <span key={item} className={ccStyles.gdprEvidenceChip}>
          {item}
        </span>
      ))}
    </div>
  );
}

export type Nis2WorkbenchQuestion = {
  questionKey?: string;
  canonicalControlCode?: string;
  localization: LocTriple;
  whyThisMatters?: LocTriple;
  evidenceHint?: LocTriple;
  recommendationHint?: LocTriple;
  remediationHint?: LocTriple;
  ownerRoleKey?: string;
  reviewCadence?: string;
  reviewCadenceLocalized?: LocTriple;
  articleRefs?: string[];
  auditorWarnings?: LocTriple;
  evidenceModel?: Record<string, LocTriple>;
};

export type Nis2EnterpriseQuestionWorkbenchProps = {
  embedded?: boolean;
  lang: Lang;
  t: (key: string, fallback?: string, opts?: Record<string, unknown>) => string;
  selectedDomainKey: string;
  selectedQuestion: Nis2WorkbenchQuestion;
  selectedQuestionPresentation?: { accent?: string };
  projectionStatus?: string;
  evidenceBucket: string;
  inferredActionsCount: number;
  answerChoice: string;
  setAnswerChoice: (v: "YES" | "PARTIAL" | "NO" | "NOT_APPLICABLE") => void;
  businessOwner: string;
  setBusinessOwner: (v: string) => void;
  technicalOwner: string;
  setTechnicalOwner: (v: string) => void;
  securityOwner: string;
  setSecurityOwner: (v: string) => void;
  assignedOwnerUserId: string;
  setAssignedOwnerUserId: (v: string) => void;
  naJustification: string;
  setNaJustification: (v: string) => void;
  linkedEvidenceIds: string[];
  setLinkedEvidenceIds: Dispatch<SetStateAction<string[]>>;
  newEvidenceIdInput: string;
  setNewEvidenceIdInput: (v: string) => void;
  evidenceSearch: string;
  setEvidenceSearch: (v: string) => void;
  govEvidence: Array<{ id: string; title?: string; freshnessStatus?: string; status?: string }>;
  govEvidenceLoading: boolean;
  govEvidenceError: string | null;
  evidenceWarnings: { yesPartialNoEvidence: boolean; naNoJustification: boolean };
  saveState: string;
  saveError: string | null;
  onSave: () => void;
  sessionId?: string | null;
  domainKey?: string | null;
  questionKey?: string | null;
  standardCode?: string | null;
};

export function Nis2EnterpriseQuestionWorkbench(props: Nis2EnterpriseQuestionWorkbenchProps) {
  const {
    embedded,
    lang,
    t,
    selectedDomainKey,
    selectedQuestion,
    selectedQuestionPresentation,
    projectionStatus,
    evidenceBucket,
    inferredActionsCount,
    answerChoice,
    setAnswerChoice,
    businessOwner,
    setBusinessOwner,
    technicalOwner,
    setTechnicalOwner,
    securityOwner,
    setSecurityOwner,
    assignedOwnerUserId,
    setAssignedOwnerUserId,
    sessionId,
    domainKey,
    questionKey,
    standardCode,
    naJustification,
    setNaJustification,
    linkedEvidenceIds,
    setLinkedEvidenceIds,
    newEvidenceIdInput,
    setNewEvidenceIdInput,
    evidenceSearch,
    setEvidenceSearch,
    govEvidence,
    govEvidenceLoading,
    govEvidenceError,
    evidenceWarnings,
    saveState,
    saveError,
    onSave,
  } = props;

  const wrap = embedded ? "mt-3 space-y-5 border-t border-gray-100 pt-3" : "mt-4 space-y-5 border-t border-gray-100 pt-4";
  const em = selectedQuestion.evidenceModel;
  const assessmentTone = deriveGdprAssessmentTone({
    presentationAccent: selectedQuestionPresentation?.accent as "green" | "amber" | "red" | "neutral" | undefined,
    projectionStatus,
  });
  const evidenceTone: GdprEnterpriseTone =
    evidenceBucket === "missing"
      ? "red"
      : evidenceBucket === "weak" || evidenceBucket === "stale_unknown"
        ? "amber"
        : "green";
  const operationalLabel = nis2OperationalStatusLabel(assessmentTone, t);
  const recordsLabel = nis2RecordsStatusLabel(evidenceTone, t);
  const optionalBullets = parseSemicolonBulletList(pickLoc(em?.optionalEvidence, lang));
  const reviewCadenceLabel = selectedQuestion.reviewCadenceLocalized
    ? pickLoc(selectedQuestion.reviewCadenceLocalized, lang)
    : selectedQuestion.reviewCadence || "";
  const nis2DomainKey = selectedDomainKey || "";
  const evidenceBindingOn =
    Boolean(featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1) && Boolean(sessionId) && Boolean(questionKey || selectedQuestion.questionKey);
  const handleLinkedChange = useCallback(
    (ids: string[]) => {
      setLinkedEvidenceIds(ids)
    },
    [setLinkedEvidenceIds],
  )

  return (
    <div className={wrap}>
      <section className={ccStyles.nis2ExecutiveStrip}>
        <div>
          <div className={ccStyles.nis2ExecutiveItemLabel}>{t(`${NIS2_GE}.executiveResilience`)}</div>
          <div className={`${ccStyles.nis2ExecutiveItemValue} ${nis2ToneExecutiveClass(assessmentTone)}`}>{operationalLabel}</div>
        </div>
        <div>
          <div className={ccStyles.nis2ExecutiveItemLabel}>{t(`${NIS2_GE}.executiveIncident`)}</div>
          <div className={`${ccStyles.nis2ExecutiveItemValue} ${nis2ToneExecutiveClass(nis2DomainKey === "NIS2_INCIDENT_RESPONSE" ? assessmentTone : "amber")}`}>
            {nis2DomainKey === "NIS2_INCIDENT_RESPONSE" ? operationalLabel : "—"}
          </div>
        </div>
        <div>
          <div className={ccStyles.nis2ExecutiveItemLabel}>{t(`${NIS2_GE}.executiveSupplier`)}</div>
          <div className={`${ccStyles.nis2ExecutiveItemValue} ${nis2ToneExecutiveClass(nis2DomainKey === "NIS2_SUPPLY_CHAIN" ? assessmentTone : "amber")}`}>
            {nis2DomainKey === "NIS2_SUPPLY_CHAIN" ? operationalLabel : "—"}
          </div>
        </div>
        <div>
          <div className={ccStyles.nis2ExecutiveItemLabel}>{t(`${NIS2_GE}.executiveRecovery`)}</div>
          <div className={`${ccStyles.nis2ExecutiveItemValue} ${nis2ToneExecutiveClass(nis2DomainKey === "NIS2_BUSINESS_CONTINUITY" ? assessmentTone : "amber")}`}>
            {nis2DomainKey === "NIS2_BUSINESS_CONTINUITY" ? operationalLabel : "—"}
          </div>
        </div>
        <div>
          <div className={ccStyles.nis2ExecutiveItemLabel}>{t(`${NIS2_GE}.executiveVulnerability`)}</div>
          <div className={`${ccStyles.nis2ExecutiveItemValue} ${nis2ToneExecutiveClass(nis2DomainKey === "NIS2_VULNERABILITY_MANAGEMENT" ? assessmentTone : "amber")}`}>
            {nis2DomainKey === "NIS2_VULNERABILITY_MANAGEMENT" ? operationalLabel : "—"}
          </div>
        </div>
        <div>
          <div className={ccStyles.nis2ExecutiveItemLabel}>{t(`${NIS2_GE}.executiveActions`)}</div>
          <div className={ccStyles.nis2ExecutiveItemValue}>
            {inferredActionsCount > 0
              ? t(`${NIS2_GE}.actionsCount`, "{{count}} suggested follow-ups", { count: inferredActionsCount })
              : t(`${NIS2_GE}.actionsNone`)}
          </div>
        </div>
      </section>

      {nis2DomainKey === "NIS2_INCIDENT_RESPONSE" ? (
        <section className={ccStyles.nis2IncidentSummary}>
          <div className={ccStyles.nis2IncidentSummaryTitle}>{t(`${NIS2_GE}.incidentSummaryTitle`)}</div>
          <p className={ccStyles.nis2IncidentSummaryBody}>{t(`${NIS2_GE}.incidentSummaryBody`)}</p>
        </section>
      ) : null}
      {nis2DomainKey === "NIS2_SUPPLY_CHAIN" ? <section className={ccStyles.nis2DomainBanner}>{t(`${NIS2_GE}.supplyChainBanner`)}</section> : null}
      {nis2DomainKey === "NIS2_VULNERABILITY_MANAGEMENT" ? (
        <section className={ccStyles.nis2DomainBanner}>{t(`${NIS2_GE}.vulnerabilityHeader`)}</section>
      ) : null}
      {nis2DomainKey === "NIS2_BUSINESS_CONTINUITY" ? (
        <section className={ccStyles.nis2DomainBanner}>{t(`${NIS2_GE}.continuityBanner`)}</section>
      ) : null}

      <header className={ccStyles.gdprQuestionHeader}>
        <h3 className={ccStyles.gdprQuestionTitle}>{pickLoc(selectedQuestion.localization, lang)}</h3>
        {selectedQuestion.whyThisMatters ? <p className={ccStyles.gdprQuestionWhy}>{pickLoc(selectedQuestion.whyThisMatters, lang)}</p> : null}
        <div className={ccStyles.gdprMetaRow}>
          {reviewCadenceLabel ? (
            <span className={ccStyles.gdprMetaChip}>
              {t(`${NIS2_GE}.reviewCadenceShort`)}: {reviewCadenceLabel}
            </span>
          ) : null}
          {selectedQuestion.ownerRoleKey ? (
            <span className={ccStyles.gdprMetaChip}>
              {t(`${NIS2_GE}.ownerRole`)}: {selectedQuestion.ownerRoleKey}
            </span>
          ) : null}
          {selectedQuestion.articleRefs?.length ? (
            <span className={ccStyles.gdprMetaChip}>
              {t(`${NIS2_GE}.articlesShort`)}: {selectedQuestion.articleRefs.join(", ")}
            </span>
          ) : null}
        </div>
        <details className={`${ccStyles.gdprAdvancedPanel} mt-3`}>
          <summary className={ccStyles.gdprAdvancedSummary}>{t(`${NIS2_GE}.additionalDetails`)}</summary>
          <div className={`${ccStyles.gdprAdvancedBody} space-y-2`}>
            {selectedQuestion.auditorWarnings ? <p>{pickLoc(selectedQuestion.auditorWarnings, lang)}</p> : null}
          </div>
        </details>
      </header>

      <article className={ccStyles.gdprEvidenceCard}>
        <h4 className="text-sm font-semibold text-slate-900">{t(`${NIS2_GE}.evidenceGuidanceTitle`)}</h4>
        <div className="mt-4 space-y-4">
          <section>
            <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${NIS2_GE}.whatToUpload`)}</div>
            <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${NIS2_GE}.primaryRecords`)}</div>
            <EvidenceChips items={NIS2_ENTERPRISE_PRIMARY_RECORDS[lang]} />
          </section>
          <section className={ccStyles.gdprEvidenceStrongCard}>
            <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${NIS2_GE}.strongIndicators`)}</div>
            <EvidenceChips items={NIS2_ENTERPRISE_STRONG_INDICATORS[lang]} />
          </section>
          <section className={ccStyles.gdprEvidenceWeakCard}>
            <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${NIS2_GE}.weakIndicators`)}</div>
            <EvidenceChips items={NIS2_ENTERPRISE_WEAK_INDICATORS[lang]} />
          </section>
          <section>
            <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${NIS2_GE}.commonProblems`)}</div>
            <ul className={ccStyles.gdprProblemsList}>
              {NIS2_ENTERPRISE_COMMON_PROBLEMS[lang].map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
          {optionalBullets.length ? (
            <details className={ccStyles.gdprAdvancedPanel}>
              <summary className={ccStyles.gdprAdvancedSummary}>{t(`${NIS2_GE}.optionalSupporting`)}</summary>
              <div className="mt-2">
                <EvidenceChips items={optionalBullets} />
              </div>
            </details>
          ) : null}
        </div>
        <details className={`${ccStyles.gdprAdvancedPanel} mt-4`}>
          <summary className={ccStyles.gdprAdvancedSummary}>{t(`${NIS2_GE}.showAdvancedEvidence`)}</summary>
          <div className={`${ccStyles.gdprAdvancedBody} mt-2 space-y-1`}>
            {em?.freshnessExpectation ? <p>{pickLoc(em.freshnessExpectation, lang)}</p> : null}
            {em?.ownershipExpectation ? <p>{pickLoc(em.ownershipExpectation, lang)}</p> : null}
            {em?.evidenceCollectionNotes ? <p>{pickLoc(em.evidenceCollectionNotes, lang)}</p> : null}
          </div>
        </details>
      </article>

      <article className={ccStyles.metricCard}>
        <div className="text-xs font-semibold text-gray-500">{t("complianceCenterUi.journey.answerLabel", "Answer")}</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(["YES", "PARTIAL", "NO", "NOT_APPLICABLE"] as const).map((opt) => {
            const label =
              opt === "YES"
                ? t("complianceCenterUi.journey.answerYes", "Yes")
                : opt === "PARTIAL"
                  ? t("complianceCenterUi.journey.answerPartial", "Partial")
                  : opt === "NO"
                    ? t("complianceCenterUi.journey.answerNo", "No")
                    : t("complianceCenterUi.journey.answerNa", "Not applicable");
            return (
              <Button
                key={opt}
                type="button"
                size="small"
                color={answerChoice === opt ? "primary" : "outline"}
                className={answerChoice === opt ? JOURNEY_PRIMARY_BTN : ""}
                onClick={() => setAnswerChoice(opt)}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </article>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="block text-xs">
          <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.businessOwner", "Business owner")}</span>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={businessOwner}
            onChange={(e) => setBusinessOwner(e.target.value)}
            placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
          />
        </label>
        <label className="block text-xs">
          <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.technicalOwner", "Technical owner")}</span>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={technicalOwner}
            onChange={(e) => setTechnicalOwner(e.target.value)}
            placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
          />
        </label>
        <label className="block text-xs">
          <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.securityOwner", "Security owner")}</span>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={securityOwner}
            onChange={(e) => setSecurityOwner(e.target.value)}
            placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
          />
        </label>
      </div>

      <label className="block text-xs">
        <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.assignedUserId", "Assigned owner user ID (optional)")}</span>
        <input
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono text-xs"
          value={assignedOwnerUserId}
          onChange={(e) => setAssignedOwnerUserId(e.target.value)}
          placeholder="UUID"
        />
      </label>

      {answerChoice === "NOT_APPLICABLE" ? (
        <label className="block text-xs">
          <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.naJustification", "Not applicable — justification")}</span>
          <textarea
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            rows={2}
            value={naJustification}
            onChange={(e) => setNaJustification(e.target.value)}
            placeholder={t("complianceCenterUi.journey.naJustificationPlaceholder", "Explain scope exclusion (auditors expect a short rationale).")}
          />
        </label>
      ) : null}

      {evidenceBindingOn && sessionId && (domainKey || selectedDomainKey) && (questionKey || selectedQuestion.questionKey) ? (
        <QuestionEvidenceBindingPanel
          sessionId={String(sessionId)}
          domainKey={String(domainKey || selectedDomainKey)}
          questionKey={String(questionKey || selectedQuestion.questionKey)}
          canonicalControlCode={selectedQuestion.canonicalControlCode}
          standardCode={standardCode || "NIS2"}
          answerValue={answerChoice}
          govEvidence={govEvidence}
          onLinkedChange={handleLinkedChange}
        />
      ) : null}

      <div className={`rounded-md border border-gray-200 bg-white p-3 text-xs ${evidenceBindingOn && sessionId ? "hidden" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold text-gray-800">{t("complianceCenterUi.journey.evidenceWorkbenchTitle", "Evidence links")}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase text-gray-700">{recordsLabel}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Link className="text-sidebarActive underline" to="/compliance-center/evidence">
            {t("complianceCenterUi.journey.linkEvidenceHub", "Open Evidence Hub")}
          </Link>
          <span className="text-gray-400">|</span>
          <Link className="text-sidebarActive underline" to="/governance/evidence">
            {t("complianceCenterUi.journey.linkGovernanceEvidence", "Governance evidence library")}
          </Link>
        </div>
        {linkedEvidenceIds.length === 0 ? (
          <p className="mt-2 text-amber-800">{t("complianceCenterUi.journey.noEvidenceLinked", "No evidence linked yet.")}</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {linkedEvidenceIds.map((id) => (
              <li key={id} className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2 py-1 font-mono text-[11px]">
                <span className="truncate">{id}</span>
                <button
                  type="button"
                  className="shrink-0 text-red-600 hover:underline"
                  onClick={() => setLinkedEvidenceIds((prev) => prev.filter((x) => x !== id))}
                >
                  {t("complianceCenterUi.journey.removeEvidenceId", "Remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            className="min-w-[12rem] flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-mono"
            value={newEvidenceIdInput}
            onChange={(e) => setNewEvidenceIdInput(e.target.value)}
            placeholder={t("complianceCenterUi.journey.addEvidenceIdPlaceholder", "Paste evidence UUID")}
          />
          <button
            type="button"
            className="rounded border border-gray-300 px-3 py-1 text-xs"
            onClick={() => {
              const v = newEvidenceIdInput.trim();
              if (!v) return;
              setLinkedEvidenceIds((prev) => (prev.includes(v) ? prev : [...prev, v]));
              setNewEvidenceIdInput("");
            }}
          >
            {t("complianceCenterUi.journey.addEvidenceId", "Add ID")}
          </button>
        </div>
        <div className="mt-3">
          <div className="font-semibold text-gray-700">{t("complianceCenterUi.journey.governanceEvidencePicker", "Link from library (this control)")}</div>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={evidenceSearch}
            onChange={(e) => setEvidenceSearch(e.target.value)}
            placeholder={t("complianceCenterUi.journey.governanceEvidenceSearch", "Search title…")}
          />
          {govEvidenceLoading ? (
            <p className="mt-2 text-gray-500">{t("complianceCenterUi.journey.governanceEvidenceLoading", "Loading evidence…")}</p>
          ) : govEvidenceError ? (
            <p className="mt-2 text-amber-800">{govEvidenceError}</p>
          ) : (
            <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto">
              {govEvidence.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-2 rounded border border-gray-50 px-2 py-1">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-medium text-gray-900">{row.title || row.id}</div>
                    <div className="text-[10px] text-gray-500">
                      {row.freshnessStatus || row.status || "—"} · {row.id}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded bg-sidebarActive px-2 py-1 text-[10px] text-white"
                    onClick={() => setLinkedEvidenceIds((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]))}
                  >
                    {t("complianceCenterUi.journey.linkThisEvidence", "Link")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {evidenceWarnings.yesPartialNoEvidence ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          {t("complianceCenterUi.journey.warnGdprEvidenceRequired")}
        </div>
      ) : null}
      {answerChoice === "NOT_APPLICABLE" && evidenceWarnings.naNoJustification ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          {t("complianceCenterUi.journey.warnNaNoJustification", "Not applicable answers should include a short justification.")}
        </div>
      ) : null}
      {answerChoice === "NO" ? (
        <div className="rounded border border-blue-100 bg-blue-50 p-2 text-xs text-blue-900">
          {t("complianceCenterUi.journey.infoNoRemediation", "NO indicates a gap: remediation is expected even if evidence is optional for this response.")}
        </div>
      ) : null}

      <div className={ccStyles.gdprStickySaveBar}>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="small" color="primary" className={JOURNEY_PRIMARY_BTN} onClick={onSave} disabled={saveState === "saving"}>
            {saveState === "saving" ? t("complianceCenterUi.journey.saving", "Saving…") : t("complianceCenterUi.journey.saveAnswer", "Save answer")}
          </Button>
          {saveState === "saved" ? <span className="self-center text-xs text-green-700">{t("complianceCenterUi.journey.saved", "Saved.")}</span> : null}
          {saveState === "error" && saveError ? <span className="self-center text-xs text-red-600">{saveError}</span> : null}
        </div>
      </div>
    </div>
  );
}
